import { FC, SyntheticEvent, useMemo, useState } from 'react';
import { useSetRecoilState } from 'recoil';
import { useMutation } from '@apollo/client';
import BootstrapForm from 'react-bootstrap/Form';
import BootstrapRow from 'react-bootstrap/Row';
import BootstrapCol from 'react-bootstrap/Col';
import { PASSWORD_STRENGTH } from 'common/constants';
import { calcPasswordStrength } from 'common/util/account';
import ELEMENT from 'common/element';
import Badge from 'components/common/Badge/Badge';
import Button, { ButtonVariant } from 'components/common/Button/Button';
import { pageAlertConfigState } from 'components/common/PageAlert/PageAlert.state';
import { PageAlertVariant } from 'components/common/PageAlert/PageAlert.types';
import { BadgeType } from '../types';
import { ACCEPT_CREATE_AND_LINK_MUTATION } from '../queries';
import * as K from '../../../common/constants';

const getErrorMessage = (strength: PASSWORD_STRENGTH): string => {
  switch (strength) {
    case PASSWORD_STRENGTH.MODERATE:
      return 'Moderate password';
    case PASSWORD_STRENGTH.STRONG:
      return 'Strong password';
    case PASSWORD_STRENGTH.WEAK:
      return 'Weak password';
    case PASSWORD_STRENGTH.INVALID:
      return 'Must be 6+ characters';
    default:
      return '';
  }
};

const badgeStyle = (strength: PASSWORD_STRENGTH): BadgeType => {
  switch (strength) {
    case PASSWORD_STRENGTH.UNDEFINED:
      return BadgeType.CUSTOM;
    case PASSWORD_STRENGTH.MODERATE:
      return BadgeType.PENDING;
    case PASSWORD_STRENGTH.STRONG:
      return BadgeType.SUCCESS;
    case PASSWORD_STRENGTH.INVALID:
    case PASSWORD_STRENGTH.WEAK:
      return BadgeType.ERROR;
    default:
      return BadgeType.CUSTOM;
  }
};

export const LinkFormCreateAccount: FC<{ token: string }> = ({ token }) => {
  const [password, setPassword] = useState('');
  const [touched, setTouched] = useState(false);

  const setPageAlertConfig = useSetRecoilState(pageAlertConfigState);

  const handleError = (message: string): void => {
    setPageAlertConfig({
      message,
      variant: PageAlertVariant.ERROR,
      autoHide: true,
      duration: 3,
    });
  };

  const [acceptInvite] = useMutation(ACCEPT_CREATE_AND_LINK_MUTATION, {
    variables: { token, password },
    onError: (error) => handleError(error.message),
    onCompleted: (data: { acceptInviteLink: boolean }) => {
      if (data.acceptInviteLink) {
        window.location.replace(K.ROUTE.ROOT);
      } else {
        handleError('Operation failed');
      }
    },
  });

  const handleSubmit = (e: SyntheticEvent) => {
    e.preventDefault();
    acceptInvite();
  };

  const onPasswordChange = (e: SyntheticEvent): void => {
    const currentTarget = e.currentTarget as HTMLInputElement;
    setPassword(currentTarget.value);
    setTouched(true);
  };

  const passwordStrength = useMemo(() => calcPasswordStrength(password), [password]);

  const isInvalidPassword = [PASSWORD_STRENGTH.UNDEFINED, PASSWORD_STRENGTH.INVALID].includes(passwordStrength);

  const inputColumn = 8;

  const inputLabel = 'Choose a password';
  const buttonLabel = 'Create Account';

  return (
    <BootstrapForm onSubmit={handleSubmit} className={'AcceptLinkInvite__Form'}>
      <BootstrapForm.Group as={BootstrapRow} className={'mb-3'}>
        <BootstrapForm.Label column>{inputLabel}</BootstrapForm.Label>
        <BootstrapCol md={inputColumn}>
          <BootstrapForm.Control
            data-test-id={ELEMENT.acceptLinkInvitePassword}
            autoComplete={'new-password'}
            type={'password'}
            value={password}
            onChange={onPasswordChange}
            isInvalid={touched && isInvalidPassword}
          />
        </BootstrapCol>
      </BootstrapForm.Group>
      <BootstrapRow className={'mb-3'}>
        <BootstrapCol />
        <BootstrapCol md={inputColumn}>
          <Badge variant={badgeStyle(passwordStrength)}>{getErrorMessage(passwordStrength)}</Badge>
        </BootstrapCol>
      </BootstrapRow>
      <BootstrapRow>
        <BootstrapCol />
        <BootstrapCol md={inputColumn}>
          <Button
            testId={ELEMENT.acceptLinkInviteButtonCreate}
            type={'submit'}
            variant={ButtonVariant.SECONDARY}
            disabled={isInvalidPassword}
          >
            {buttonLabel}
          </Button>
        </BootstrapCol>
      </BootstrapRow>
    </BootstrapForm>
  );
};
